'use client';

import { Sheet, SheetClose, SheetContent, SheetHeader } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { useEducationalTutorial } from '../../hooks/useEducationalTutorial';
import { EducationalTutorialCloseButton } from '../CloseButton/EducationalTutorialCloseButton';
import { EducationalTutorialContent } from '../EducationalTutorialContent/EducationalTutorialContent';
import { EducationalTutorialImage } from '../Image/EducationalTutorialImage';

type EducationalSheetProps = ComponentProps<'div'> & {
  onComplete?: () => void;
};

function EducationalSheet({ onComplete, ...props }: EducationalSheetProps) {
  const t = useTranslations('global_dialogs.educational');
  const tutorial = useEducationalTutorial({ onComplete });

  return (
    <Sheet open={tutorial.isOpen} onOpenChange={tutorial.closeDialog}>
      <SheetContent
        side="bottom"
        className="gap-6 rounded-2xl bg-background"
        showCloseButton={false}
        data-track-container="Guide"
        data-track-feature="Education"
        onKeyDown={tutorial.handleKeyDown}
        onPointerDownOutside={event => event.preventDefault()}
        onInteractOutside={event => event.preventDefault()}
      >
        <SheetHeader className="py-0 px-0 relative h-40">
          <SheetClose asChild>
            <EducationalTutorialCloseButton tutorial={tutorial} />
          </SheetClose>
          <EducationalTutorialImage stepKey={tutorial.stepKey} className="absolute inset-x-0 top-0" />
        </SheetHeader>
        <EducationalTutorialContent
          tutorial={tutorial}
          variant="sheet"
          className="p-6 min-h-55"
          key={`educational-guide-${tutorial.stepKey}`}
          data-track-step={tutorial.currentStep}
          data-track-title={t(`${tutorial.stepKey}.title`)}
          {...props}
        />
      </SheetContent>
    </Sheet>
  );
}

export { EducationalSheet };
